import React, { useEffect, useState } from 'react';
import { MapPin, PartyPopper, ArrowRight, CalendarDays } from 'lucide-react';

const EVENT_DATE = new Date('2026-08-29T10:00:00+02:00').getTime();

const calc = () => {
  const diff = EVENT_DATE - Date.now();
  if (diff <= 0) return null;
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff / 3600000) % 24),
    m: Math.floor((diff / 60000) % 60),
    s: Math.floor((diff / 1000) % 60)
  };
};

export const EventHero = () => {
  const [t, setT] = useState(calc);

  useEffect(() => {
    const timer = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(timer);
  }, []);

  if (!t) return null;

  return (
    <section
      className="relative py-16 md:py-24 overflow-hidden"
      data-testid="event-hero"
      style={{
        background:
          'radial-gradient(ellipse at top left, rgba(220,38,38,0.15) 0%, transparent 50%), radial-gradient(ellipse at bottom right, rgba(245,158,11,0.12) 0%, transparent 50%), linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
      }}
    >
      {/* Dekorative Muster */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'repeating-linear-gradient(45deg, transparent 0, transparent 24px, rgba(255,255,255,0.08) 24px, rgba(255,255,255,0.08) 25px)'
          }}
        ></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Save the Date Ribbon */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 font-bold text-sm uppercase tracking-[0.2em] shadow-xl shadow-amber-500/30">
            <PartyPopper className="w-4 h-4" />
            Save the Date
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Linke Seite: Kalenderblatt */}
          <div className="flex justify-center lg:justify-end">
            <div className="relative">
              {/* Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/40 via-amber-500/30 to-red-600/40 blur-3xl scale-90"></div>

              {/* Kalenderblatt */}
              <div
                className="relative w-[280px] md:w-[340px] rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500"
                data-testid="event-calendar-card"
              >
                {/* Header rot */}
                <div className="bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white text-center py-4 border-b-4 border-white/20">
                  <p className="text-sm md:text-base uppercase tracking-[0.25em] font-bold">
                    Freitag
                  </p>
                </div>
                {/* Body weiß */}
                <div className="bg-white text-center py-8 px-6">
                  <p className="text-7xl md:text-8xl font-black text-gray-900 leading-none tabular-nums">
                    29
                  </p>
                  <p className="mt-3 text-xl md:text-2xl font-bold text-gray-800 uppercase tracking-wider">
                    August
                  </p>
                  <p className="mt-1 text-lg text-gray-500 font-medium">2026</p>
                </div>
              </div>
            </div>
          </div>

          {/* Rechte Seite: Text + CTAs */}
          <div className="text-center lg:text-left">
            <p className="text-amber-300 text-xs md:text-sm uppercase tracking-[0.25em] font-bold mb-4">
              Große Neueröffnung
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5">
              Willkommen am neuen{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-500">
                Omoda &amp; Jaecoo
              </span>{' '}
              Standort
            </h2>
            <p className="text-base md:text-lg text-white/70 leading-relaxed mb-6">
              Feiern Sie mit uns die offizielle Eröffnung unseres neuen
              Standorts im Florapark Magdeburg. Entdecken Sie die aufregenden
              Modelle der neuen Marken bei einem exklusiven Event mit
              Erfrischungen und persönlicher Beratung.
            </p>

            {/* Location Card */}
            <div className="inline-flex items-start gap-3 px-5 py-3 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm mb-8 text-left">
              <MapPin className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-semibold">Florapark Magdeburg</p>
                <p className="text-white/60 text-sm">Olvenstedter Graseweg 37, 39128 Magdeburg</p>
              </div>
            </div>

            {/* Mini-Countdown */}
            <div className="flex justify-center lg:justify-start gap-3 md:gap-4 mb-8">
              {[
                { v: t.d, l: 'Tage' },
                { v: t.h, l: 'Std' },
                { v: t.m, l: 'Min' },
                { v: t.s, l: 'Sek' }
              ].map((u, i) => (
                <div
                  key={i}
                  className="flex flex-col items-center px-3 py-2 md:px-4 md:py-3 rounded-lg bg-white/10 border border-white/20 backdrop-blur-sm min-w-[60px] md:min-w-[72px]"
                  data-testid={`event-countdown-${u.l.toLowerCase()}`}
                >
                  <span className="font-mono text-2xl md:text-3xl font-bold text-white tabular-nums leading-none">
                    {String(u.v).padStart(2, '0')}
                  </span>
                  <span className="mt-1 text-[10px] uppercase tracking-wider text-white/50 font-semibold">
                    {u.l}
                  </span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <a
                href="https://oj.autohaus-wilke.de"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold shadow-xl shadow-red-500/30 hover:shadow-red-500/50 transition-all duration-300 hover:scale-[1.03]"
                data-testid="event-cta-website"
              >
                Zur Omoda &amp; Jaecoo Website
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="https://www.google.com/maps/dir//Olvenstedter+Graseweg+37,+39128+Magdeburg"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold backdrop-blur-sm transition-all duration-300"
                data-testid="event-cta-route"
              >
                <MapPin className="w-4 h-4" />
                Route planen
              </a>
              <a
                href="https://wa.me/4939163577134?text=Hallo,%20ich%20möchte%20zur%20Er%C3%B6ffnung%20am%2029.08.2026%20im%20Florapark%20kommen.%20Bitte%20informieren%20Sie%20mich%20über%20Details."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#25D366]/20 hover:bg-[#25D366]/30 border border-[#25D366]/40 text-white font-semibold backdrop-blur-sm transition-all duration-300"
                data-testid="event-cta-rsvp"
              >
                <CalendarDays className="w-4 h-4" />
                Ich komme
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
